// components/footfall/planToSlides.ts
// "Send to Slides": the scenario on the map becomes a Slides presentation. The plan of each level it touches is drawn
// here, in the browser, as the landlord's own plans are drawn, and uploaded; the tenants' and the proposed brands'
// logos are placed over it as their own elements, so they stay editable in Slides.
import type { useKernel } from '@/kernel/Kernel';
import { uploadFileToCloud } from '#lib/azure-client';
import { categoryEntry, unitName, type PulsePlace } from '#lib/footfall/pulse';
import { campaignLabel } from '#lib/leasing/campaignCard';
import { buildPlanDeck, PLAN_PROPOSAL, PLAN_PURPLE, type PlanDeckLevel } from '#lib/footfall/planDeck';
import { childrenOf } from '#lib/footfall/layout';
import type { CampaignDTO, PulseMove, SlideDeckDTO } from '#types/types';
import { valueAsset } from '#lib/analyst/valuation';
import { derivePulseFromState } from './pulseDerive';
import { loadShortlist, savePlan } from './pulseState';

type Kernel = NonNullable<ReturnType<typeof useKernel>['kernel']['current']>;
const NAVY = '#2B2F6B';
const PX = 2400;

/** Draws one level's units: let ones outlined, vacancies in the landlord's purple with code and area, proposals in amber. */
async function drawLevel(places: PulsePlace[], moves: Map<string, PulseMove>, logoOf: (place: PulsePlace) => string | null, fr: boolean): Promise<Omit<PlanDeckLevel, 'label' | 'imageUrl'> & { blob: Blob }> {
  // Framed on the mall: a level also holds outdoor units across the site, which would shrink the mall to its middle.
  // Units whose centre lies beyond twice the median distance from the median centre are left out of the frame.
  const centre = (p: PulsePlace) => { const r = p.feature.rings.flat(); return [r.reduce((s, q) => s + q[0], 0) / r.length, r.reduce((s, q) => s + q[1], 0) / r.length] as const; };
  const median = (v: number[]) => [...v].sort((a, b) => a - b)[Math.floor(v.length / 2)]!;
  const centres = places.map(centre);
  const [mx, my] = [median(centres.map(c => c[0])), median(centres.map(c => c[1]))];
  const dist = centres.map(([x, y]) => Math.hypot((x - mx) * Math.cos(my * Math.PI / 180), y - my));
  const limit = 2 * median(dist);
  places = places.filter((_, i) => dist[i]! <= limit);
  const all = places.flatMap(p => p.feature.rings.flat());
  const lat0 = all.reduce((s, p) => s + p[1], 0) / all.length;
  const kx = Math.cos(lat0 * Math.PI / 180);
  // Turned so the mall's long axis runs across the page, as a landlord turns its plan to fill the sheet.
  const flat = all.map(([lng, lat]) => [lng * kx, lat] as const);
  const [cx0, cy0] = [flat.reduce((s, p) => s + p[0], 0) / flat.length, flat.reduce((s, p) => s + p[1], 0) / flat.length];
  let sxx = 0, syy = 0, sxy = 0;
  for (const [x, y] of flat) { sxx += (x - cx0) ** 2; syy += (y - cy0) ** 2; sxy += (x - cx0) * (y - cy0); }
  const angle = Math.atan2(2 * sxy, sxx - syy) / 2, cos = Math.cos(-angle), sin = Math.sin(-angle);
  const turn = (lng: number, lat: number): [number, number] => { const x = lng * kx - cx0, y = lat - cy0; return [x * cos - y * sin, x * sin + y * cos]; };
  const turned = all.map(([lng, lat]) => turn(lng, lat));
  const xs = turned.map(p => p[0]), ys = turned.map(p => p[1]);
  const [minX, maxX, minY, maxY] = [Math.min(...xs), Math.max(...xs), Math.min(...ys), Math.max(...ys)];
  const pad = 0.03, spanX = (maxX - minX) * (1 + 2 * pad), spanY = (maxY - minY) * (1 + 2 * pad);
  const width = PX, height = Math.round(PX * spanY / spanX);
  const at = ([lng, lat]: [number, number]): [number, number] => { const [x, y] = turn(lng, lat); return [((x - minX) / spanX + pad / (1 + 2 * pad)) * width, (1 - ((y - minY) / spanY + pad / (1 + 2 * pad))) * height]; };
  const canvas = document.createElement('canvas');
  canvas.width = width; canvas.height = height;
  const ctx = canvas.getContext('2d')!;
  ctx.fillStyle = '#FFFFFF'; ctx.fillRect(0, 0, width, height);
  const logos: PlanDeckLevel['logos'] = [];
  for (const place of places) {
    const move = moves.get(place.feature.id);
    const vacant = place.category === 'VACANT' && !move;
    ctx.beginPath();
    for (const ring of place.feature.rings) ring.forEach((p, i) => { const [x, y] = at(p); if (i) ctx.lineTo(x, y); else ctx.moveTo(x, y); });
    ctx.closePath();
    ctx.fillStyle = move?.type === 'relet' ? PLAN_PROPOSAL : move?.type === 'vacate' ? '#CFCFCF' : vacant ? PLAN_PURPLE : '#FFFFFF';
    ctx.fill('evenodd');
    ctx.lineWidth = 2; ctx.strokeStyle = NAVY; ctx.stroke();
    const pts = place.feature.rings.reduce((a, b) => b.length > a.length ? b : a).map(at);
    const [cx, cy] = [pts.reduce((s, p) => s + p[0], 0) / pts.length, pts.reduce((s, p) => s + p[1], 0) / pts.length];
    const bw = Math.max(...pts.map(p => p[0])) - Math.min(...pts.map(p => p[0])), bh = Math.max(...pts.map(p => p[1])) - Math.min(...pts.map(p => p[1]));
    const logo = logoOf(place);
    if (logo) { logos.push({ url: logo, name: place.name ?? '', cx: cx / width, cy: cy / height, side: Math.min(bw, bh) * 0.62 / width }); continue; }
    // Vacancies and proposals read their unit and area, as on the landlord's plan; a let shop with no logo its name.
    const label = vacant || move ? `${unitName(place.feature)}\n${Math.round(place.feature.areaSqm).toLocaleString(fr ? 'fr-FR' : 'en-GB')} m²` : place.name ?? '';
    if (!label) continue;
    const size = Math.max(10, Math.min(26, Math.min(bw, bh) / 4));
    ctx.font = `${vacant ? 'bold ' : ''}${size}px Helvetica, Arial, sans-serif`;
    ctx.fillStyle = vacant ? '#FFFFFF' : '#1A1A1A'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    label.split('\n').forEach((line, i, lines) => ctx.fillText(line, cx, cy + (i - (lines.length - 1) / 2) * size * 1.15, bw * 0.95));
  }
  const blob = await new Promise<Blob | null>(resolve => canvas.toBlob(resolve, 'image/png'));
  if (!blob) throw new Error('The plan could not be drawn.');
  return { blob, aspect: width / height, logos };
}

/**
 * An image Slides can read: a PNG or JPEG on our storage. Slides reads only those two (its PDF export embeds nothing
 * else) and refuses an outside image served without an image type, as Altarea's logo is. So a logo elsewhere is copied
 * onto our storage, and one in another format (SVG, WebP) is drawn to a PNG first; one that cannot be read is dropped.
 */
const hosted = new Map<string, Promise<string | null>>();
function hostImage(url: string | null | undefined): Promise<string | null> {
  if (!url) return Promise.resolve(null);
  if (!hosted.has(url)) hosted.set(url, (async () => {
    try {
      const res = await fetch(url);
      if (!res.ok) return null;
      const bytes = new Uint8Array(await res.arrayBuffer());
      const type = bytes[0] === 0x89 && bytes[1] === 0x50 ? 'image/png' : bytes[0] === 0xff && bytes[1] === 0xd8 ? 'image/jpeg' : null;
      const ours = url.startsWith('/') || /\.blob\.core\.windows\.net\//.test(url);
      if (type) return ours ? url : await uploadFileToCloud(new Blob([bytes], { type }), `logo.${type === 'image/png' ? 'png' : 'jpg'}`, type);
      const source = URL.createObjectURL(new Blob([bytes], { type: res.headers.get('content-type') || 'image/svg+xml' }));
      try {
        const img = new Image();
        img.src = source;
        await img.decode();
        const scale = Math.min(1, 512 / Math.max(img.naturalWidth || 512, img.naturalHeight || 512));
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round((img.naturalWidth || 512) * scale)); canvas.height = Math.max(1, Math.round((img.naturalHeight || 512) * scale));
        canvas.getContext('2d')!.drawImage(img, 0, 0, canvas.width, canvas.height);
        const png = await new Promise<Blob | null>(resolve => canvas.toBlob(resolve, 'image/png'));
        return png ? await uploadFileToCloud(png, 'logo.png', 'image/png') : null;
      } finally { URL.revokeObjectURL(source); }
    } catch { return null; }
  })());
  return hosted.get(url)!;
}

/** Builds the presentation of the scenario on the map and opens it in Slides. Returns the refusal, if any. */
export async function sendScenarioToSlides(k: Kernel, onStep?: (step: string) => void): Promise<string | null> {
  const fr = k.getState().language === 'fr';
  const first = k.getState().pulse.draft;
  if (first?.propertyId) {
    // A scenario's shortlist and pricing are only right once its run's shortlist has loaded: sent just after a
    // run switch, the deck said "0 ranked brands" and priced the moves at €0 (2026-10-25).
    onStep?.(fr ? 'Chargement de la short-list' : 'Loading the shortlist');
    void loadShortlist(k, first.propertyId, first.campaignId ?? null);
    for (let waited = 0; derivePulseFromState(k.getState()).shortlistStatus === 'loading' && waited < 30_000; waited += 250) await new Promise(r => setTimeout(r, 250));
    if (first.campaignId && !k.getState().inventory.Campaign.registry[first.campaignId]) await k.syncEntity('Campaign', first.campaignId).catch(() => undefined);
  }
  const state = k.getState();
  const w = derivePulseFromState(state);
  const plan = state.pulse.draft;
  const scenario = w.scenarios.find(s => s.active);
  if (!plan || !w.property || !w.venue || !scenario) return fr ? 'Ouvrez un plan avant de l’envoyer.' : 'Open a plan before sending it.';
  if (!scenario.moves.length && !scenario.layout.length) return fr ? 'Ce scénario ne propose encore rien.' : 'This scenario proposes nothing yet.';
  if (w.shortlistStatus !== 'ready') return fr ? 'La short-list du scénario n’a pas pu être chargée : réessayez.' : 'The scenario’s shortlist could not be loaded: try again.';

  const moves = new Map(w.scenario.moves.map(m => [m.shopId, m]));
  const shortlistLogo = new Map(w.shortlist.map(b => [b.id, b.logoUrl ?? null]));
  const logoOf = (p: PulsePlace) => { const m = moves.get(p.feature.id); return m?.type === 'relet' ? (m.brandId ? shortlistLogo.get(m.brandId) ?? null : null) : m ? null : p.logoUrl ?? null; };
  const touched = new Set([...w.scenario.moves.map(m => m.shopId), ...w.layoutRows.flatMap(r => childrenOf(r.change))]);
  const levels = w.levels.filter(l => w.places.some(p => p.feature.level === l.id && touched.has(p.feature.id)));
  const drawn: PlanDeckLevel[] = [];
  for (const level of levels.length ? levels : w.levels.filter(l => l.ordinal === 0).slice(0, 1)) {
    onStep?.(fr ? `Dessin du plan, niveau ${level.name}` : `Drawing the plan, level ${level.name}`);
    const { blob, aspect, logos } = await drawLevel(w.places.filter(p => p.feature.level === level.id), moves, logoOf, fr);
    const imageUrl = await uploadFileToCloud(blob, `plan-${plan.id}-${level.id}.png`, 'image/png');
    if (!imageUrl) return fr ? 'Le plan dessiné n’a pas pu être enregistré.' : 'The drawn plan could not be uploaded.';
    const readable = await Promise.all(logos.map(async l => ({ ...l, url: await hostImage(l.url) })));
    drawn.push({ label: `${fr ? 'Niveau' : 'Level'} ${level.name}`, imageUrl, aspect, logos: readable.flatMap(l => l.url ? [{ ...l, url: l.url }] : []) });
  }

  onStep?.(fr ? 'Mise en page de la présentation' : 'Laying out the presentation');
  const portfolioId = (w.property as { portfolioId?: string | null }).portfolioId ?? null;
  if (portfolioId) await k.ensureInventoryIds('Portfolio', [portfolioId], { force: false, batchSize: 1 }).catch(() => undefined);
  const portfolio = portfolioId ? (k.getState().inventory.Portfolio.registry as Record<string, { name?: string; logoUrl?: string | null } | undefined>)[portfolioId] : undefined;
  const campaign = plan.campaignId ? k.getState().inventory.Campaign.registry[plan.campaignId] as CampaignDTO | undefined : undefined;
  const placed = new Set(w.scenario.moves.flatMap(m => m.brandId ? [m.brandId] : []));
  const unitOf = (id: string) => { const f = w.places.find(p => p.feature.id === id)?.feature ?? w.todayPlaces.find(p => p.feature.id === id)?.feature; return f ? unitName(f) : id; };
  const shortlistLogos = new Map(await Promise.all(w.shortlist.slice(0, 40).map(async b => [b.id, await hostImage(b.logoUrl)] as const)));
  const doc = buildPlanDeck({
    fr,
    origin: { module: 'plan', planId: plan.id, planName: plan.name, scenarioName: scenario.name, propertyId: w.property.id, propertyName: w.property.name, sentAt: new Date().toISOString() },
    centreLogoUrl: await hostImage((w.property as { logoUrl?: string | null }).logoUrl),
    landlord: { name: portfolio?.name ?? '', logoUrl: await hostImage(portfolio?.logoUrl) },
    thesis: scenario.thesis,
    totals: {
      valueCreated: w.scenario.yieldPct ? w.totals.valueCreated : null, uplift: w.totals.uplift, works: w.totals.works,
      occupancyToday: `${w.totals.occupancyToday.let}/${w.totals.occupancyToday.total}`, occupancyScenario: `${w.totals.occupancyScenario.let}/${w.totals.occupancyScenario.total}`,
    },
    assumptions: { referenceRent: w.scenario.referenceRent, yieldPct: w.scenario.yieldPct },
    income: w.property.rentRoll && w.property.analystAssumptions ? valueAsset(w.property.rentRoll, w.property.analystAssumptions).income : null,
    mockup: !!w.property.analystAssumptions?.mockup || w.totals.estimatedRents > 0,
    levels: drawn,
    moves: w.priced.map(r => ({ unit: unitOf(r.move.shopId), areaSqm: r.areaSqm, brand: r.move.brandName ?? '–', rentPerSqm: r.move.rentPerSqm, uplift: r.uplift, kind: r.move.type })),
    layout: w.layoutRows.map(r => r.change.kind === 'split'
      ? `${fr ? 'Diviser' : 'Split'} ${unitOf(r.change.shopId)} ${fr ? 'en' : 'into'} ${r.change.shares.length}`
      : `${fr ? 'Réunir' : 'Merge'} ${r.change.shopIds.map(unitOf).join(' + ')}`),
    shortlist: {
      label: campaign ? campaignLabel(campaign, fr ? 'Recherche Source' : 'Source run') : (fr ? 'Toutes les recherches Source du centre' : 'All the centre\'s Source runs'),
      total: w.shortlist.length,
      rows: w.shortlist.map(b => ({ name: b.name, logoUrl: shortlistLogos.get(b.id) ?? null, trade: b.category ? categoryEntry(b.category)[fr ? 'fr' : 'en'] : '–', fit: b.fitScore, size: b.surfaceMin || b.surfaceMax ? `${b.surfaceMin ?? '?'}–${b.surfaceMax ?? '?'} m²` : '–', placed: placed.has(b.id) })),
    },
  });

  onStep?.(fr ? 'Création dans Slides' : 'Creating it in Slides');
  const res = await fetch('/api/slides/deck', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    // Slides names a deck, on screen and in its export, after its file: a presentation's is its own name.
    body: JSON.stringify({ name: `${plan.name} · ${scenario.name}`.slice(0, 120), sourceFileName: `${plan.name} · ${scenario.name}`.slice(0, 120), guidelineKey: 'inline_unit', doc }),
  });
  if (res.status === 403) return fr ? 'Slides n’est pas ouvert à ce compte.' : 'Slides is not enabled for this account.';
  if (!res.ok) return fr ? 'La présentation n’a pas pu être créée dans Slides.' : 'The presentation could not be created in Slides.';
  const { deck } = await res.json() as { deck?: SlideDeckDTO };
  if (!deck) return fr ? 'La présentation n’a pas pu être créée dans Slides.' : 'The presentation could not be created in Slides.';
  // The plan is saved first; then the deck opens through its own link, as a shared Slides link does: a fresh load
  // is the one path that lands in Slides whatever the Plan conversation was holding on screen.
  if (k.getState().pulse.editVersion !== k.getState().pulse.savedVersion) await savePlan(k);
  window.location.assign(`/?sd=${encodeURIComponent(deck.projectId)}`);
  return null;
}
